import { RootState, Task } from 'constants/interfaces'
import { Post } from 'redux/reducers/feed'
import { keyframes } from '@emotion/core'

// ============= COLOR UTILITIES =============

// Pastel colors for drop shadow
const PASTEL_COLORS = [
  'rgba(255, 130, 190, 0.75)', // Soft pink
  'rgba(120, 160, 240, 0.75)', // Soft blue
  'rgba(200, 120, 220, 0.75)', // Soft orchid
  'rgba(255, 150, 110, 0.75)', // Soft coral
  'rgba(90, 220, 200, 0.75)', // Soft turquoise
  'rgba(255, 120, 150, 0.75)', // Soft rose
  'rgba(160, 130, 220, 0.75)', // Soft purple
  'rgba(255, 170, 90, 0.75)', // Soft orange
  'rgba(255, 190, 120, 0.75)', // Soft peach
  'rgba(140, 200, 240, 0.75)', // Soft sky blue
  'rgba(220, 140, 180, 0.75)', // Soft mauve
  'rgba(180, 220, 140, 0.75)', // Soft lime
  'rgba(255, 140, 140, 0.75)', // Soft red
  'rgba(140, 180, 220, 0.75)', // Soft steel blue
  'rgba(220, 180, 140, 0.75)', // Soft tan
] as const

/**
 * Get pastel color by index
 * Returns a consistent color for the same index
 */
export const getPastelColorShadow = (index: number): string => {
  return PASTEL_COLORS[index % PASTEL_COLORS.length]
}

/**
 * Generate CSS drop shadow filter with pastel color
 * @param colorIndex - Index to determine color
 * @param intensity - 'normal' for PersonalView items, 'subtle' for drawer
 */
export const getDropShadowFilter = (colorIndex: number, intensity: 'normal' | 'subtle' = 'normal'): string => {
  const color = getPastelColorShadow(colorIndex)

  if (intensity === 'subtle') {
    return `drop-shadow(0 0 10px ${color}) drop-shadow(0 0 35px ${color})`
  }

  return `drop-shadow(0 0 15px ${color}) drop-shadow(0 0 45px ${color})`
}

// ============= TIMESTAMP UTILITIES =============

/**
 * Normalize chat timestamps (remove leading "2" if timestamp > 10000000000)
 * Example: 21762073550 -> 1762073550
 */
export const normalizeTimestamp = (timestamp: number): number => {
  if (timestamp > 10000000000) {
    return timestamp % 10000000000
  }
  return timestamp
}

// ============= CHAT UTILITIES =============

/**
 * Check if chat has unread messages
 * Compares last message in chat against viewed messages for current user
 */
export const checkChatHasUnread = (
  chat: Post,
  commentsState: RootState['comments']['commentsState'],
  viewedCommentsIds: RootState['comments']['viewedCommentsIds']
): boolean => {
  const postId = chat.post_id
  const chatMessages = commentsState?.[postId]?.items ?? []
  const viewedIds = viewedCommentsIds?.[postId] ?? []
  const lastMessage = chatMessages.length > 0 ? chatMessages[chatMessages.length - 1] : null
  return lastMessage && viewedIds.length > 0 ? !viewedIds.includes(lastMessage?.cid) : false
}

// ============= STATISTICS UTILITIES =============

/**
 * Calculate user statistics for PersonalView drawer
 */
export interface UserStats {
  urgentTasks: number
  unreadPosts: number
  uncompletedTasks: number
}

export const calculateUserStats = (tasks: Task[], posts: Post[]): UserStats => {
  const urgentTasksCount = tasks.filter((task) => task.hours_until_end < 0).length

  return {
    urgentTasks: urgentTasksCount,
    unreadPosts: posts.length,
    uncompletedTasks: tasks.length,
  }
}

// ============= TASK SORTING UTILITIES =============

/**
 * Sort tasks by hours until end (ascending)
 */
export const sortTasksByDeadline = (tasks: Task[]): Task[] => {
  return [...tasks].sort((a, b) => a.hours_until_end - b.hours_until_end)
}

/**
 * Filter and sort urgent tasks (overdue)
 */
export const getUrgentTasks = (tasks: Task[]): Task[] => {
  return tasks.filter((task) => task.hours_until_end < 0).sort((a, b) => a.hours_until_end - b.hours_until_end)
}

/**
 * Filter and sort uncompleted tasks (not overdue)
 */
export const getUncompletedTasks = (tasks: Task[]): Task[] => {
  return tasks.filter((task) => task.hours_until_end >= 0).sort((a, b) => a.hours_until_end - b.hours_until_end)
}

// ============= BADGE UTILITIES =============

export type BadgeType = 'overdue' | 'hours' | 'none'

/**
 * Determine badge type and value based on tasks
 * Priority: 1. Overdue tasks, 2. Urgent tasks (< 24h), 3. None
 */
export const getTaskBadge = (tasks: Task[]): { type: BadgeType; value?: number } => {
  if (tasks.length === 0) {
    return { type: 'none' }
  }

  // Check for overdue tasks (negative hours)
  const overdueTasks = tasks.filter((task) => task.hours_until_end < 0)
  if (overdueTasks.length > 0) {
    return { type: 'overdue' }
  }

  // Check for urgent tasks (< 24 hours)
  const urgentTasks = tasks.filter((task) => task.hours_until_end >= 0 && task.hours_until_end < 24)
  if (urgentTasks.length > 0) {
    // Find the task with the least time remaining
    const minHours = Math.min(...urgentTasks.map((t) => t.hours_until_end))
    return { type: 'hours', value: Math.floor(minHours) }
  }

  return { type: 'none' }
}

// ============= ANIMATION UTILITIES =============

/**
 * Generate floating animation parameters based on index
 * Each item gets a unique animation pattern, duration, and delay
 */
export const getFloatingAnimation = (index: number) => {
  // Different animation patterns with more active movements
  const patterns = [
    // Pattern 1: Vertical floating with rotation
    keyframes`
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(0, -8px) rotate(1deg); }
    `,
    // Pattern 2: Horizontal floating with slight tilt
    keyframes`
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      50% { transform: translate(7px, 0) rotate(-1deg); }
    `,
    // Pattern 3: Diagonal floating with scale
    keyframes`
      0%, 100% { transform: translate(0, 0) scale(1); }
      50% { transform: translate(6px, -6px) scale(1.02); }
    `,
    // Pattern 4: Circular motion
    keyframes`
      0% { transform: translate(0, 0) rotate(0deg); }
      25% { transform: translate(5px, -5px) rotate(0.5deg); }
      50% { transform: translate(7px, 0) rotate(1deg); }
      75% { transform: translate(5px, 5px) rotate(0.5deg); }
      100% { transform: translate(0, 0) rotate(0deg); }
    `,
    // Pattern 5: Dynamic bounce
    keyframes`
      0%, 100% { transform: translate(0, 0) rotate(0deg); }
      33% { transform: translate(-4px, -6px) rotate(-0.8deg); }
      66% { transform: translate(4px, -7px) rotate(0.8deg); }
    `,
    // Pattern 6: Figure-eight motion
    keyframes`
      0% { transform: translate(0, 0); }
      25% { transform: translate(6px, -4px); }
      50% { transform: translate(0, -6px); }
      75% { transform: translate(-6px, -4px); }
      100% { transform: translate(0, 0); }
    `,
  ]

  const pattern = patterns[index % patterns.length]
  // Vary duration between 5-9 seconds (faster than before)
  const duration = 8 + (index % 5)
  // Vary delay between 0-2 seconds
  const delay = (index % 4) * 0.5

  return { pattern, duration, delay }
}
